import React from "react";
import { Box, Dialog, IconButton, useTheme } from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CourseWizard from "../Course/CourseWizard";
import { CourseWizardData, EMPTY_COURSE_DATA } from "../Course/types";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { CreatedDraftLo } from "./CreateActivityModalCst";

interface CreateCourseModalCstProps {
  open: boolean;
  onClose: () => void;
  onCreated: (item: CreatedDraftLo) => void;
}

interface ContentProps {
  onClose: () => void;
  onCreated: (item: CreatedDraftLo) => void;
}

const CreateCourseModalContent: React.FC<ContentProps> = ({
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation("wizard-course");
  const { id: corporateId = "" } = useParams();
  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "course",
  });

  const handleComplete = async (data: CourseWizardData) => {
    const res = await saveDraft(data);
    onCreated({
      id: res.id,
      title: data.title,
      type: "COURSE",
      entity: { ...data, id: res.id, is_draft: true },
    });
    onClose();
  };

  return (
    <CourseWizard
      embedded
      mode="add"
      initialData={EMPTY_COURSE_DATA}
      onComplete={handleComplete}
      isSubmitting={isSavingDraft}
      completeLabel={t("confirm") || "Conferma"}
      syncNavbarInitiative={false}
    />
  );
};

const CreateCourseModalCst: React.FC<CreateCourseModalCstProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: theme.palette.text.primary,
          zIndex: 1,
        }}
      >
        <X size={20} />
      </IconButton>
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        {open && (
          <CreateCourseModalContent onClose={onClose} onCreated={onCreated} />
        )}
      </Box>
    </Dialog>
  );
};

export default CreateCourseModalCst;
